import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { describeAudience, SpecraAudiencePicker } from '@mono/ui-components/captains';
import { SpecraButton } from '@mono/ui-components/ensigns';
import { SpecraAudienceChip, SpecraTooltip } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { toast } from 'sonner';
import { useHearsayAudienceDirectory } from './audienceDirectory';
import { useSetIntelClaimAudience } from './hooks';
const ONLY_ME = {
    everyone: false,
    targets: []
};
export const toChipLevel = (level)=>level.toLowerCase();
const shortDay = (d)=>`${d.getUTCDate()} ${d.toLocaleDateString('en-US', {
        month: 'short',
        timeZone: 'UTC'
    })}`;
export function audienceDetail(audience) {
    if (audience.changeCount <= 1) return undefined;
    return `Changed by ${audience.changedByName}${audience.changedAt ? ` · ${shortDay(audience.changedAt)}` : ''}`;
}
const hasSuggestedTeam = (t)=>t.kind !== '';
/**
 * "Who can see this" on one claim (REG-03 v2.2). The author gets the chip as
 * a control, a one-click "Share with team" while it is personal, and the
 * picker; everyone else gets a read-only chip with "Shared by {author}".
 * The server re-checks author + hearsay.write on every change.
 */ export function ClaimAudienceControl({ claim, size = 'xs', onChanged, pickerOpen, onPickerOpenChange, 'data-testid': testId }) {
    const [anchor, setAnchor] = useState(null);
    const [ownOpen, setOwnOpen] = useState(false);
    const open = pickerOpen ?? ownOpen;
    const setOpen = (next)=>{
        setOwnOpen(next);
        onPickerOpenChange?.(next);
    };
    const isAuthor = claim.viewerIsAuthor;
    const directory = useHearsayAudienceDirectory(isAuthor && open);
    const setAudience = useSetIntelClaimAudience(onChanged);
    if (!isAuthor) {
        // The read-only chip does not forward DOM props, so the testid sits on a wrapper.
        return /*#__PURE__*/ _jsx("span", {
            "data-testid": testId ?? Testids.registryHearsay.audienceChip,
            children: /*#__PURE__*/ _jsx(SpecraAudienceChip, {
                readOnly: true,
                size: size,
                level: toChipLevel(claim.audience.level),
                summary: claim.audience.summary,
                sharedBy: claim.recordedByName
            })
        });
    }
    const current = {
        everyone: claim.audience.level === 'EVERYONE',
        targets: claim.audience.targets
    };
    const suggested = hasSuggestedTeam(claim.suggestedTeam) ? claim.suggestedTeam : undefined;
    const save = (audience, after)=>setAudience.mutate({
            claim,
            audience,
            optimisticSummary: describeAudience(audience)
        }, {
            onSuccess: (saved)=>after?.(saved)
        });
    const shareWithTeam = ()=>{
        if (!suggested) {
            setOpen(true);
            return;
        }
        save({
            everyone: false,
            targets: [
                suggested
            ]
        }, (saved)=>{
            toast.success(`Shared with ${suggested.label}.`, {
                duration: 8000,
                action: {
                    label: 'Undo',
                    onClick: ()=>setAudience.mutate({
                            claim: saved,
                            audience: ONLY_ME,
                            optimisticSummary: describeAudience(ONLY_ME)
                        })
                }
            });
        });
    };
    return /*#__PURE__*/ _jsxs("span", {
        className: "inline-flex flex-wrap items-center gap-[var(--space-1)]",
        children: [
            /*#__PURE__*/ _jsx(SpecraAudienceChip, {
                ref: setAnchor,
                size: size,
                level: toChipLevel(claim.audience.level),
                summary: claim.audience.summary,
                detail: audienceDetail(claim.audience),
                onActivate: ()=>setOpen(true),
                "data-testid": testId ?? Testids.registryHearsay.audienceChip
            }),
            claim.audience.level === 'PERSONAL' && (suggested ? /*#__PURE__*/ _jsx(SpecraTooltip, {
                content: `Share with ${suggested.label}`,
                children: /*#__PURE__*/ _jsx(SpecraButton, {
                    variant: "ghost",
                    size: "xs",
                    onClick: shareWithTeam,
                    disabled: setAudience.isPending,
                    "data-testid": Testids.registryHearsay.shareWithTeam,
                    children: "Share with team"
                })
            }) : /*#__PURE__*/ _jsx(SpecraButton, {
                variant: "ghost",
                size: "xs",
                onClick: shareWithTeam,
                "data-testid": Testids.registryHearsay.shareWithTeam,
                children: "Share…"
            })),
            open && /*#__PURE__*/ _jsx(SpecraAudiencePicker, {
                mode: "popover",
                open: open,
                onOpenChange: setOpen,
                anchor: anchor,
                value: current,
                suggestedTeam: suggested,
                saving: setAudience.isPending,
                onChange: (next)=>{
                    save(next);
                    setOpen(false);
                },
                "data-testid": Testids.registryHearsay.audiencePicker,
                ...directory
            })
        ]
    });
}
